/**
 * 1.7.0 stream C (docs/dev/formats-1.7.0-build.md): Details → Format…: the primary size (presets or custom), add,
 * rename, resize and remove formats, Make primary, the subtitle height per format and (1.7.1) the framing per format
 * (Per clip, or Fit / Fill / Blur for the whole storyline; a new 1:1 or 4:5 format starts with Blur). Needs the edit
 * lock; every change is an op through `apply`, one undo step each (typing a label is one step, sealed when the field is
 * left).
 *
 * Adding the first format while storyline clips are Fit offers to fill the storyline in it (a Fit 16:9 clip is
 * letterboxed in 9:16 until it gets Fill there): the format and the Fill are one step.
 */
import { useState } from 'react';
import { ArrowUpToLine, Plus, Trash2 } from 'lucide-react';
import {
  COMMON_ASPECTS, FIT_LABELS, MAX_FORMATS, PRIMARY_FORMAT, SEQUENCE_FORMAT_GROUPS, VIDEO_FIT_MODES, aspectLabel, defaultFormatFit, defaultFormatSize, formatProblem, formatsOf, ops, sameAspect, sequenceSizeProblem,
  type FitMode, type ResolvedFormat, type SequenceDocument, type Size,
} from '@prism/contracts';
import { Modal } from '../../components/Modal';
import type { EditorApply } from './formatContext';
import { addFormatWithFill, fitClipsIn, formatColor } from './edit-formats';
import { SubtitleHeightField } from './SubtitleHeight';

export interface FormatDialogProps {
  doc: SequenceDocument;
  open: boolean;
  onClose: () => void;
  apply: EditorApply;
  /** The edit lock is held. */
  canEdit: boolean;
  /** 1.7.0 stream C addition: ends an undo step (leaving a label field). */
  seal?: () => void;
}

const num = (v: string) => (v.trim() === '' ? NaN : Number(v));

/** A W × H pair of fields with a button; the problem (formatProblem) under them. */
function SizeFields({ prefix, initial, problemOf, onApply, label, disabled }: {
  prefix: string; initial: Size; problemOf: (s: Size) => string | null; onApply: (s: Size) => void; label: string; disabled?: boolean;
}) {
  const [w, setW] = useState(initial.width ? String(initial.width) : '');
  const [h, setH] = useState(initial.height ? String(initial.height) : '');
  const size = { width: num(w), height: num(h) };
  const same = size.width === initial.width && size.height === initial.height;
  const problem = Number.isFinite(size.width) && Number.isFinite(size.height) ? problemOf(size) : 'Type a width and a height';
  return (
    <div className="format-size">
      <div className="format-size-row">
        <input className="input mono" inputMode="numeric" aria-label="Width" value={w} onChange={(e) => setW(e.target.value)} disabled={disabled} data-testid={`${prefix}-width`} />
        <span className="faint">×</span>
        <input className="input mono" inputMode="numeric" aria-label="Height" value={h} onChange={(e) => setH(e.target.value)} disabled={disabled} data-testid={`${prefix}-height`} />
        <button type="button" className="btn sm" disabled={disabled || same || Boolean(problem)} onClick={() => onApply(size)} data-testid={`${prefix}-apply`}>{label}</button>
      </div>
      {!same && <span className={problem ? 'error-text field-hint' : 'help'} data-testid={`${prefix}-problem`}>{problem ?? `${size.width}×${size.height} · ${aspectLabel(size)}`}</span>}
    </div>
  );
}

/** The framing of a format for its whole storyline (1.7.1): Per clip (left out), or Fit, Fill or Blur for every V1 clip. */
function FormatFit({ f, apply }: { f: ResolvedFormat; apply: EditorApply }) {
  return (
    <label className="format-subs" title="Fit, Fill or Blur for every storyline clip in this format, or set clip by clip in the inspector">
      <span className="faint">Framing</span>
      <select className="select sm" value={f.fit ?? ''} aria-label={`Framing of ${f.label}`} data-testid={`format-fit-${f.id}`}
        onChange={(e) => apply((d) => ops.updateFormat(d, f.id, { fit: (e.target.value || null) as FitMode | null }))}>
        <option value="">Per clip</option>
        {VIDEO_FIT_MODES.map((m) => <option key={m} value={m}>{FIT_LABELS[m]}</option>)}
      </select>
    </label>
  );
}

export function FormatDialog({ doc, open, onClose, apply, canEdit, seal }: FormatDialogProps) {
  const [fill, setFill] = useState(true);
  const [customOpen, setCustomOpen] = useState(false);
  if (!open) return null;
  const formats = formatsOf(doc);
  const primary = formats[0]!;
  const others = formats.slice(1);
  const full = others.length >= MAX_FORMATS;
  const offerFill = others.length === 0 && fitClipsIn(doc, PRIMARY_FORMAT).length > 0;
  const add = (size: Size & { label?: string }) => apply((d) => addFormatWithFill(d, size, offerFill && fill));
  const presetSizes = SEQUENCE_FORMAT_GROUPS.map((g) => ({ id: g.id, label: g.label, width: g.width, height: g.height }));
  const presetOf = presetSizes.find((p) => p.width === doc.width && p.height === doc.height)?.id ?? 'custom';
  return (
    <Modal title="Sequence formats" onClose={onClose} wide actions={<button className="btn" onClick={onClose} data-testid="format-dialog-close">Done</button>}>
      <div data-testid="format-dialog" className="format-dialog">
        {!canEdit && <div className="banner info" data-testid="format-dialog-readonly"><div className="banner-body">Start editing the sequence (the edit lock) to change its formats.</div></div>}
        <p className="help" style={{ margin: 0 }}>The primary format is the sequence's own size. Each other format reframes the same footage for another shape; export renders any of them. Every change here can be undone.</p>
        <fieldset disabled={!canEdit} className="format-fieldset">
          <section className="format-section" data-testid="format-primary">
            <p className="section-label"><span className="format-swatch" style={{ background: formatColor(0) }} aria-hidden /> Primary · {primary.width}×{primary.height} · {primary.aspect}</p>
            <div className="field-row" style={{ alignItems: 'flex-start' }}>
              <div className="field">
                <label htmlFor="format-primary-preset">Size</label>
                <select id="format-primary-preset" className="select" value={presetOf} data-testid="format-primary-preset"
                  onChange={(e) => {
                    const p = presetSizes.find((x) => x.id === e.target.value);
                    if (!p) { setCustomOpen(true); return; }
                    setCustomOpen(false);
                    if (!formatProblem(doc, p, PRIMARY_FORMAT)) apply((d) => ops.setPrimarySize(d, p.width, p.height));
                  }}>
                  {presetSizes.map((p) => <option key={p.id} value={p.id} disabled={Boolean(formatProblem(doc, p, PRIMARY_FORMAT)) && presetOf !== p.id}>{p.label}</option>)}
                  <option value="custom">Custom size…</option>
                </select>
                {(customOpen || presetOf === 'custom') && (
                  <SizeFields key={`${doc.width}x${doc.height}`} prefix="format-primary" initial={primary} label="Set size"
                    problemOf={(s) => sequenceSizeProblem(s.width, s.height) ?? formatProblem(doc, s, PRIMARY_FORMAT)}
                    onApply={(s) => apply((d) => ops.setPrimarySize(d, s.width, s.height))} />
                )}
                <span className="help">The frame rate stays {doc.frameRate.num / doc.frameRate.den === Math.round(doc.frameRate.num / doc.frameRate.den) ? doc.frameRate.num / doc.frameRate.den : (doc.frameRate.num / doc.frameRate.den).toFixed(2)} fps. Reframing keys keep their meaning in any shape.</span>
              </div>
              <div className="format-side-fields">
                <FormatFit f={primary} apply={apply} />
                <SubtitleHeightField f={primary} apply={apply} seal={seal} />
              </div>
            </div>
          </section>

          <section className="format-section" data-testid="format-list">
            <p className="section-label">Other formats ({others.length}/{MAX_FORMATS})</p>
            {!others.length && <p className="muted" style={{ margin: 0 }}>None yet. Add a vertical or square version below; the timeline stays one edit.</p>}
            {others.map((f) => (
              <div className="format-row" key={f.id} data-testid="format-row" data-format={f.id}>
                <span className="format-swatch" style={{ background: formatColor(f.index) }} aria-hidden />
                <input className="input format-label" value={doc.formats!.find((x) => x.id === f.id)!.label ?? ''} placeholder={f.aspect} maxLength={60} aria-label={`Name of ${f.label}`} data-testid={`format-label-${f.id}`}
                  onChange={(e) => apply((d) => ops.updateFormat(d, f.id, { label: e.target.value || null }), `format-label:${f.id}`)} onBlur={seal} />
                <SizeFields key={`${f.width}x${f.height}`} prefix={`format-size-${f.id}`} initial={f} label="Resize"
                  problemOf={(s) => formatProblem(doc, s, f.id)} onApply={(s) => apply((d) => ops.updateFormat(d, f.id, { width: s.width, height: s.height }))} />
                <FormatFit f={f} apply={apply} />
                <SubtitleHeightField f={f} apply={apply} seal={seal} />
                <span style={{ flex: 1 }} />
                <button type="button" className="btn ghost sm" onClick={() => apply((d) => ops.promoteFormat(d, f.id))} data-testid={`format-promote-${f.id}`}
                  title={`Make ${f.label} the sequence's own size; ${primary.aspect} becomes one of its formats, with its framing kept`}><ArrowUpToLine size={13} /> Make primary</button>
                <button type="button" className="btn ghost icon sm danger" onClick={() => apply((d) => ops.removeFormat(d, f.id))} aria-label={`Remove ${f.label}`} data-testid={`format-remove-${f.id}`}
                  title="Remove this format and its framing (undo brings it back)"><Trash2 size={13} /></button>
              </div>
            ))}
          </section>

          <section className="format-section" data-testid="format-add">
            <p className="section-label">Add a format</p>
            <div className="format-add-row">
              {COMMON_ASPECTS.map((a) => {
                const size = defaultFormatSize(doc, a);
                const problem = formatProblem(doc, size);
                const taken = sameAspect(a, doc) || (doc.formats ?? []).some((f) => sameAspect(f, a));
                return (
                  <button key={a.id} type="button" className="btn sm" disabled={Boolean(problem)} onClick={() => add(size)} data-testid={`format-add-${a.id}`}
                    title={problem ?? `${size.width}×${size.height}${defaultFormatFit(size) ? ' · starts with Blur for the whole format' : ''}`}><Plus size={13} /> {a.label}{taken ? '' : <span className="faint mono"> {size.width}×{size.height}</span>}</button>
                );
              })}
            </div>
            <div className="field">
              <label>Custom size</label>
              <SizeFields prefix="format-custom" initial={{ width: 0, height: 0 }} label="Add" disabled={full}
                problemOf={(s) => formatProblem(doc, s)} onApply={(s) => add(s)} />
            </div>
            {offerFill && (
              <label className="toggle-row" data-testid="format-fill-offer">
                <input type="checkbox" checked={fill} onChange={(e) => setFill(e.target.checked)} data-testid="format-fill-storyline" />
                <span>Switch the storyline to Fill in a new 9:16 or custom format ({fitClipsIn(doc, PRIMARY_FORMAT).length} Fit clip{fitClipsIn(doc, PRIMARY_FORMAT).length === 1 ? '' : 's'}; otherwise they are letterboxed there). 1:1 and 4:5 start with Blur for the whole format.</span>
              </label>
            )}
          </section>
        </fieldset>
      </div>
    </Modal>
  );
}
